import { Check } from 'lucide-react';
import React from 'react';
import {
	ListBox as AriaListBox,
	ListBoxItem as AriaListBoxItem,
	type ListBoxProps as AriaListBoxProps,
	Collection,
	Header,
	type ListBoxItemProps,
	ListBoxSection,
	type SectionProps,
	composeRenderProps
} from 'react-aria-components';
import { cn, focusRing } from '~/shared/components/utils';

export function ListBox<T extends object>({ children, ...props }: Omit<AriaListBoxProps<T>, 'layout' | 'orientation'>) {
	return (
		<AriaListBox
			{...props}
			className={composeRenderProps(props.className, className => cn('rounded-lg border border-gray-200 p-1 outline-0', className))}
		>
			{children}
		</AriaListBox>
	);
}

export function itemStyles(renderProps: {
	isSelected?: boolean;
	isDisabled?: boolean;
	isFocusVisible?: boolean;
	isFocusWithin?: boolean;
	isInvalid?: boolean;
}) {
	return cn(
		'group relative flex cursor-default select-none items-center gap-8 rounded-md px-2.5 py-1.5 text-sm will-change-transform',
		focusRing(renderProps),
		renderProps.isSelected
			? 'bg-primary-500 text-white -outline-offset-4 outline-white [&+[data-selected]]:rounded-t-none [&:has(+[data-selected])]:rounded-b-none'
			: 'text-gray-700 -outline-offset-2 hover:bg-gray-100',
		renderProps.isDisabled && 'text-gray-300'
	);
}

export function ListBoxItem(props: ListBoxItemProps) {
	const textValue = props.textValue || (typeof props.children === 'string' ? props.children : undefined);
	return (
		<AriaListBoxItem {...props} textValue={textValue} className={renderProps => itemStyles(renderProps)}>
			{composeRenderProps(props.children, children => (
				<>
					{children}
					<div className="absolute inset-x-4 bottom-0 hidden h-px bg-white/20 [.group[data-selected]:has(+[data-selected])_&]:block" />
				</>
			))}
		</AriaListBoxItem>
	);
}

export function dropdownItemStyles(renderProps: { isDisabled?: boolean; isFocused?: boolean; isOpen?: boolean }) {
	return cn(
		'group flex cursor-default select-none items-center gap-4 rounded-lg py-2 pl-3 pr-1 text-sm outline outline-0',
		renderProps.isDisabled ? 'text-gray-300' : 'text-gray-900',
		renderProps.isFocused && 'bg-primary-500 text-white',
		!renderProps.isFocused && renderProps.isOpen && 'bg-gray-50'
	);
}

export function DropdownItem(props: ListBoxItemProps & { description?: string }) {
	const textValue = props.textValue || (typeof props.children === 'string' ? props.children : undefined);
	return (
		<AriaListBoxItem
			{...props}
			textValue={textValue}
			className={composeRenderProps(props.className, (className, renderProps) => cn(dropdownItemStyles(renderProps), className))}
		>
			{composeRenderProps(props.children, (children, { isSelected }) => (
				<>
					{props.description ? (
						<div className="flex flex-1 flex-col items-start gap-1">
							<span className="group-selected:font-semibold">{children}</span>
							<span className="text-xs text-gray-500">{props.description}</span>
						</div>
					) : (
						<span className="flex flex-1 items-center gap-2 truncate font-normal group-selected:font-semibold">{children}</span>
					)}
					<span className="flex w-5 items-center">{isSelected && <Check className="size-4" />}</span>
				</>
			))}
		</AriaListBoxItem>
	);
}

export interface DropdownSectionProps<T> extends SectionProps<T> {
	title?: string;
	items?: T[];
}

export function DropdownSection<T extends object>(props: DropdownSectionProps<T>) {
	return (
		<ListBoxSection className="after:block after:h-[5px] after:content-[''] first:mt-[5px]">
			<Header className="sticky top-[5px] z-10 -mx-1 -mt-px truncate border-y border-gray-100 bg-gray-50/60 px-4 py-1 text-sm font-semibold text-gray-500 backdrop-blur-md supports-[-moz-appearance:none]:bg-gray-50 [&+*]:mt-1">
				{props.title}
			</Header>
			<Collection items={props.items}>{props.children}</Collection>
		</ListBoxSection>
	);
}
